import React, { useState } from 'react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { FileDownload, TableChart, PictureAsPdf, Image as ImageIcon } from '@mui/icons-material';
import { COLORS } from '../../Styles/Colors';
import {
  exportWidgetCsv,
  exportWidgetPdf,
  exportWidgetPng,
  WidgetExportOptions,
} from '../../Utils/widgetExport';

interface WidgetExportMenuProps {
  /** Built lazily on click so the chart <svg> and filters are current. */
  getExportOptions: () => WidgetExportOptions;
  disabled?: boolean;
  onError?: (message: string) => void;
}

export const WidgetExportMenu: React.FC<WidgetExportMenuProps> = ({ getExportOptions, disabled, onError }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const run = async (exporter: (opts: WidgetExportOptions) => void | Promise<void>) => {
    setAnchorEl(null);
    try {
      await exporter(getExportOptions());
    } catch (error) {
      console.error('Widget export failed:', error);
      onError?.(error instanceof Error ? error.message : 'Export failed');
    }
  };

  return (
    <>
      <Tooltip title={disabled ? 'No data to export' : 'Export'}>
        <span>
          <IconButton
            size="small"
            aria-label="Export widget"
            disabled={disabled}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            // Keep the parent card's drag-and-drop from hijacking the button.
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ color: COLORS.teal }}
          >
            <FileDownload fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => run(exportWidgetCsv)}>
          <ListItemIcon><TableChart fontSize="small" /></ListItemIcon>
          <ListItemText>Download as CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => run(exportWidgetPdf)}>
          <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
          <ListItemText>Download as PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => run(exportWidgetPng)}>
          <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download as Image (PNG)</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default WidgetExportMenu;
